import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Check, ImageOff } from "lucide-react";
import { formatCAD } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Button } from "./ui/button";
import { toast } from "../hooks/use-toast";

const PLACEHOLDER =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">
      <rect width="400" height="400" fill="#f1f5f9"/>
      <text x="200" y="200" text-anchor="middle" fill="#94a3b8" font-family="sans-serif" font-size="16">No image</text>
    </svg>`
  );

export default function ProductCard({ product }) {
  const { addToCart } = useStore();
  const [imgSrc, setImgSrc] = useState(product.image || PLACEHOLDER);
  const [failed, setFailed] = useState(false);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
    toast({
      title: "Added to cart",
      description: product.name,
    });
  };

  return (
    <Link
      to={`/produit/${product.id}`}
      className="group card-lift bg-white border border-border rounded-xl overflow-hidden flex flex-col"
    >
      <div className="relative aspect-square bg-secondary/40 overflow-hidden">
        {failed ? (
          <div className="h-full w-full flex flex-col items-center justify-center text-slate-400 gap-2">
            <ImageOff className="h-8 w-8" />
            <span className="text-xs">Image unavailable</span>
          </div>
        ) : (
          <img
            src={imgSrc}
            alt={product.name}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => {
              setFailed(true);
              setImgSrc(PLACEHOLDER);
            }}
            className="h-full w-full object-contain p-3 group-hover:scale-105 transition-transform duration-300"
          />
        )}
        {!product.inStock && (
          <span className="absolute top-2 left-2 bg-slate-800 text-white text-[11px] px-2 py-1 rounded-md">
            Special order
          </span>
        )}
        {product.inStock && (
          <span className="absolute top-2 left-2 bg-emerald-600 text-white text-[11px] px-2 py-1 rounded-md flex items-center gap-1">
            <Check className="h-3 w-3" /> In stock
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-rp-green">
          {product.brand}
        </span>
        <h3 className="mt-1 text-sm font-medium text-slate-900 leading-snug line-clamp-2 min-h-[2.5rem] group-hover:text-rp-green">
          {product.name}
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">Part # {product.sku}</p>
        <div className="mt-auto pt-3 flex items-center justify-between gap-2">
          <div>
            <span className="text-lg font-bold text-slate-900">
              {formatCAD(product.price)}
            </span>
            {product.onSale && (
              <span className="block text-xs text-slate-400 line-through">
                {formatCAD(product.regularPrice)}
              </span>
            )}
          </div>
          <Button
            onClick={handleAdd}
            size="sm"
            className="rounded-full bg-rp-green hover:bg-rp-green-dark gap-1 shrink-0"
          >
            <ShoppingCart className="h-4 w-4" />
            Add
          </Button>
        </div>
      </div>
    </Link>
  );
}
